"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "../common/Table";
import Button from "./Button";
import { 
  useGetBookingTypes, 
  useAddBookingType, 
  useUpdateBookingType, 
  useUpdateBookingTypeStatus, 
  useDeleteBookingTypes 
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface BookingType {
  id: number;
  booking_type: string;
  menu_order: number | null;
  status: number;
  created_by: number;
  modified_by: number | null;
  created_date: string;
  modified_date: string;
  ip: string | null;
}

const MasterBookingType: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBookingType, setCurrentBookingType] = useState<BookingType | null>(null);
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  
  // Form states
  const [bookingType, setBookingType] = useState<string>("");
  const [menuOrder, setMenuOrder] = useState<string>("");
  const [status, setStatus] = useState<number>(1);

  // API Hooks
  const getBookingTypes = useGetBookingTypes();
  const addBookingType = useAddBookingType();
  const updateBookingType = useUpdateBookingType();
  const updateStatus = useUpdateBookingTypeStatus();
  const deleteBookingTypes = useDeleteBookingTypes(() => {
    // Success callback
    fetchBookingTypes();
    setSelectedRows([]);
  });
  
  const user = useAuth();
  const loggedInUser = user?.user?.id;

  // Fetch booking types on component mount
  useEffect(() => {
    fetchBookingTypes();
  }, []);

  const fetchBookingTypes = () => {
    getBookingTypes.mutate(
      {},
      {
        onSuccess: (data) => {
          if (data.success) {
            setBookingTypes(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching booking types:", error);
        },
      }
    );
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setCurrentBookingType(null);
      setBookingType("");
      setMenuOrder("");
    
    }
  }, [isModalOpen]);

  // Set form data when currentBookingType changes (for editing)
  useEffect(() => {
    if (currentBookingType && isModalOpen) {
      setBookingType(currentBookingType.booking_type || "");
      setMenuOrder(currentBookingType.menu_order?.toString() || "");
     
    }
  }, [currentBookingType, isModalOpen]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!bookingType.trim()) {
      alert("Please enter booking type");
      return;
    }

    try {
      const payload = {
        booking_type: bookingType.trim(),
        menu_order: menuOrder ? parseInt(menuOrder) : null,
        status: 1,
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentBookingType) {
        // Update existing booking type
        updateBookingType.mutate(
          {
            ...payload,
            auto_id: currentBookingType.id,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchBookingTypes();
                setIsModalOpen(false);
                setCurrentBookingType(null);
              }
            },
            onError: (error) => {
              alert("Failed to update booking type. Please try again.");
            },
          }
        );
      } else {
        // Add new booking type
        addBookingType.mutate(
          payload,
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchBookingTypes();
                setIsModalOpen(false);
              }
            },
            onError: (error) => {
              alert("Failed to add booking type. Please try again.");
            },
          }
        );
      }
    } catch (error) {
      alert("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser,
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchBookingTypes();
            setSelectedRows([]);
          }
        },
        onError: (error) => {
          alert("Failed to publish booking types. Please try again.");
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser,
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchBookingTypes();
            setSelectedRows([]);
          }
        },
        onError: (error) => {
          alert("Failed to unpublish booking types. Please try again.");
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    const message = selectedRows.length === 1 
      ? "Are you sure you want to delete this booking type?"
      : `Are you sure you want to delete ${selectedRows.length} selected booking types?`;

    if (confirm(message)) {
      deleteBookingTypes.mutate(
        {
          id: selectedRows.join(","),
          user_id: loggedInUser
        },
        {
          onError: (error) => {
            alert("Failed to delete booking type(s). Please try again.");
          }
        }
      );
    }
  };

  // Transform API data to match table structure
  const tableData = bookingTypes.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    bookingType: item.booking_type,
    menuOrder: item.menu_order,
    status: item.status,
    createdBy: item.created_by,
    createdDate: item.created_date,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "bookingType", header: "Booking Type", sortable: true },
    { 
      key: "menuOrder", 
      header: "Menu Order", 
      sortable: true,
      render: (order: number | null) => order || "N/A"
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (item: any) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                item === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm">
              {item === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    { 
      key: "createdBy", 
      header: "Created By", 
      sortable: true,
      render: (userId: number) => `User ${userId}`
    },
    {
      key: "createdDate",
      header: "Created Date",
      sortable: true,
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentBookingType(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (confirm("Are you sure you want to delete this booking type?")) {
                  // Delete single booking type directly
                  deleteBookingTypes.mutate(
                    {
                      id: row.id.toString(),
                      user_id: loggedInUser
                    },
                    {
                      onError: (error) => {
                        alert("Failed to delete booking type. Please try again.");
                      }
                    }
                  );
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentBookingType(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Booking Type
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || deleteBookingTypes.isPending}
          >
            {deleteBookingTypes.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={getBookingTypes.isPending}
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Menu Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentBookingType(null);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentBookingType
                    ? "Update Booking Type"
                    : "Add Booking Type"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentBookingType(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Booking Type */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Booking Type *
                    </label>
                    <input
                      type="text"
                      value={bookingType || ""}
                      onChange={(e) => setBookingType(e.target.value)}
                      placeholder="Enter booking type"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Menu Order */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Menu Order
                    </label>
                    <input
                      type="number"
                      value={menuOrder || ""}
                      onChange={(e) => setMenuOrder(e.target.value)}
                      placeholder="Enter menu order"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Status */}
                  {/* <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div> */}

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={
                        addBookingType.isPending || updateBookingType.isPending
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addBookingType.isPending || updateBookingType.isPending
                        ? "Processing..."
                        : currentBookingType
                        ? "Update Booking Type"
                        : "Add Booking Type"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterBookingType;